/**
 * Schedule Stop Component
 *
 * Renders a single time slot row in the schedule table.
 * Displays time range, location details, and action buttons.
 *
 * @element peach-schedule-stop
 *
 * @fires location-selected - When user clicks "RESERVE YOUR PEACHES"
 */

import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { StopWithLocation } from "../services/calendar-data";
import "@/features/ui/card";
import "@/features/ui/icon";

@customElement("peach-schedule-stop")
export class ScheduleStopComponent extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Object })
  declare stop: StopWithLocation;

  @property({ type: Number })
  declare distance: number | null;

  constructor() {
    super();
    this.stop = null as unknown as StopWithLocation;
    this.distance = null;
  }

  private handleGoToLocation(e: Event) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent("location-selected", {
        detail: {
          locationId: this.stop.location.id,
          stopId: this.stop.id,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleGetDirections(e: Event) {
    e.stopPropagation();
    const { lat, lng } = this.stop.location;
    window.open(`https://maps.google.com/?q=${lat},${lng}`, "_blank");
  }

  /**
   * Render inventory status badge
   */
  private renderInventoryBadge() {
    const { inventory_status } = this.stop;
    const isSoldOut = inventory_status.sold_out || this.stop.overfilled;

    if (isSoldOut && this.stop.walk_up) {
      return html`
        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-soft text-warning-text ">
          Preorders Closed
        </span>
      `;
    }

    if (isSoldOut) {
      return html`
        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-muted text-muted ">
          Preorders Closed
        </span>
      `;
    }

    if (inventory_status.hurry_up) {
      return html`
        <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-soft text-warning-text ">
          Limited
        </span>
      `;
    }

    return null;
  }

  render() {
    if (!this.stop) return null;

    const { location, start_time, end_time } = this.stop;

    return html`
      <peach-card
        variant="interactive"
        @click=${this.handleGoToLocation}
      >
        <div class="flex items-start justify-between gap-2">
          <h3 class="font-semibold leading-snug">
            ${location.name}
          </h3>
          ${this.distance !== null
            ? html`<span class="text-sm text-subtle flex-shrink-0">${this.distance.toFixed(1)} mi</span>`
            : null}
        </div>

        <p class="text-sm mb-1">
          <peach-icon name="map-pin" size="sm" class="text-brand"></peach-icon>
          ${location.address}
        </p>

        <div class="flex items-center gap-1">
          <peach-icon name="calendar" size="sm" class="text-brand"></peach-icon>
          <span class="px-2 py-0.5 rounded text-xs font-medium bg-brand-accent text-white">
            ${start_time} – ${end_time}
          </span>
          ${this.renderInventoryBadge()}
        </div>
      </peach-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "peach-schedule-stop": ScheduleStopComponent;
  }
}
